import React, { useState, useEffect, useRef } from 'react';
import type { Recipe, MenuItem, InventoryItem } from '../../types';
import XCircleIcon from '../icons/XCircleIcon';
import PlusIcon from '../icons/PlusIcon';
import TrashIcon from '../icons/TrashIcon';

/** Cost is always per kg or per L (user's requirement). */
const COST_ONLY_UNITS = ['kg', 'L'] as const;
function normalizeUnit(unit: string | undefined): typeof COST_ONLY_UNITS[number] {
  const u = (unit ?? 'kg').toLowerCase();
  if (u === 'l') return 'L';
  if (u === 'kg' || u === 'g' || u === 'ml' || u === 'units') return 'kg';
  return 'kg';
}

type RecipeIngredient = Recipe['ingredients'][number];

interface RecipeManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (recipe: Recipe) => void;
  menuItem: MenuItem | null;
  recipeInventoryMode: 'inventory_connected' | 'cost_only';
  inventoryItems: InventoryItem[];
  existingRecipe?: Recipe | null;
  /** Other menu items and their recipes, used to copy ingredient list from another dish (names/links only; quantity and price left for user to enter). */
  otherMenuItemsWithRecipes?: { menuItem: MenuItem; recipe: Recipe }[];
}

const RecipeManagementModal: React.FC<RecipeManagementModalProps> = ({
  isOpen,
  onClose,
  onSave,
  menuItem,
  recipeInventoryMode,
  inventoryItems,
  existingRecipe,
  otherMenuItemsWithRecipes = []
}) => {
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [copyFromMenuItemId, setCopyFromMenuItemId] = useState<string>('');
  const copyFromSelectRef = useRef<HTMLSelectElement>(null);
  /** Per-row display strings so decimal entry (e.g. "0.5") is not lost while typing */
  const [editingQty, setEditingQty] = useState<Record<number, string>>({});
  const [editingCost, setEditingCost] = useState<Record<number, string>>({});

  useEffect(() => {
    if (existingRecipe) {
      setIngredients(existingRecipe.ingredients);
    } else {
      setIngredients([]);
    }
    setEditingQty({});
    setEditingCost({});
  }, [existingRecipe, isOpen]);

  if (!isOpen || !menuItem) return null;

  const isCostOnly = recipeInventoryMode === 'cost_only';

  const handleAddIngredient = () => {
    if (isCostOnly) {
      setIngredients([...ingredients, { quantity: 1, name: '', unit: 'kg', costPerUnit: 0 }]);
      return;
    }
    if (inventoryItems.length === 0) {
      alert('No inventory items available. Please add inventory items first.');
      return;
    }
    setIngredients([...ingredients, { inventoryItemId: inventoryItems[0].id, quantity: 1 }]);
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
    setEditingQty({});
    setEditingCost({});
  };

  const handleUpdateIngredient = (index: number, updates: Partial<RecipeIngredient>) => {
    const updated = [...ingredients];
    updated[index] = { ...updated[index], ...updates };
    setIngredients(updated);
  };

  const handleSave = () => {
    if (ingredients.length === 0) {
      if (window.confirm('No ingredients added. Do you want to save an empty recipe?')) {
        onSave({ menuItemId: menuItem.id, ingredients: [] });
        handleClose();
      }
      return;
    }

    if (isCostOnly) {
      const invalid = ingredients.some(ing => ing.quantity <= 0 || (ing.costPerUnit ?? 0) < 0);
      if (invalid) {
        alert('Please ensure all ingredients have quantity > 0 and cost per unit ≥ 0.');
        return;
      }
    } else {
      const invalidIngredients = ingredients.filter(
        ing => ing.inventoryItemId == null || ing.quantity <= 0
      );
      if (invalidIngredients.length > 0) {
        alert('Please ensure all ingredients have valid inventory items and quantities greater than 0.');
        return;
      }
    }

    onSave({ menuItemId: menuItem.id, ingredients });
    handleClose();
  };

  const handleClose = () => {
    setIngredients([]);
    onClose();
  };

  const getInventoryItemName = (id: number) => {
    const item = inventoryItems.find(i => i.id === id);
    return item ? item.name : 'Unknown';
  };

  const getInventoryItemUnit = (id: number) => {
    const item = inventoryItems.find(i => i.id === id);
    return item ? item.unit : '';
  };

  const getIngredientCost = (ing: RecipeIngredient): number => {
    if (ing.inventoryItemId != null) {
      const item = inventoryItems.find(i => i.id === ing.inventoryItemId);
      return (item?.costPerUnit ?? 0) * ing.quantity;
    }
    return (ing.costPerUnit ?? 0) * ing.quantity;
  };

  const totalCost = ingredients.reduce((sum, ing) => sum + getIngredientCost(ing), 0);

  /** Copy ingredient list from another recipe (ingredient identity only; quantity and cost set to 0 for user to fill). */
  const handleCopyFromRecipe = () => {
    const rawValue = copyFromSelectRef.current?.value ?? copyFromMenuItemId;
    const selectedId = rawValue === '' ? null : Number(rawValue);
    if (selectedId == null || Number.isNaN(selectedId)) {
      alert('Please select a dish to copy from.');
      return;
    }
    const entry = otherMenuItemsWithRecipes.find(
      (r) => Number(r.menuItem.id) === selectedId
    );
    if (!entry) {
      alert('Selected dish could not be found. Please try again.');
      return;
    }
    const sourceRecipe = entry.recipe;
    const sourceList = isCostOnly
      ? sourceRecipe.ingredients
      : sourceRecipe.ingredients.filter((ing): ing is typeof ing & { inventoryItemId: number } => ing.inventoryItemId != null);
    if (sourceList.length === 0) {
      if (isCostOnly) {
        alert('That recipe has no ingredients to copy.');
      } else {
        alert('That recipe has no inventory-linked ingredients. Use a recipe that uses inventory items, or add ingredients manually.');
      }
      return;
    }
    const copied: RecipeIngredient[] = sourceList.map((ing) => {
      if (isCostOnly) {
        const name = ing.name ?? (ing.inventoryItemId != null ? inventoryItems.find(i => i.id === ing.inventoryItemId)?.name ?? '' : '');
        const rawUnit = ing.unit ?? (ing.inventoryItemId != null ? inventoryItems.find(i => i.id === ing.inventoryItemId)?.unit : undefined);
        return { name, unit: normalizeUnit(rawUnit), quantity: 0, costPerUnit: 0 };
      }
      return {
        inventoryItemId: ing.inventoryItemId,
        quantity: 0,
      };
    });
    setIngredients(copied);
    setCopyFromMenuItemId('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            Recipe for: {menuItem.name}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            {isCostOnly ? (
              <p className="text-sm text-gray-600 mb-4">
                Cost-only mode: define product name, quantity, unit and cost per unit for food cost. Inventory will not be depleted.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                Define the ingredients and quantities needed to make this menu item.
                When an order is paid, inventory will be automatically consumed based on these recipes.
              </p>
            )}
          </div>

          {otherMenuItemsWithRecipes.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">Copy ingredients from another dish</label>
              <p className="text-xs text-gray-500 mb-2">Select a dish, then click Copy to pre-fill the list. You only need to enter quantity and cost.</p>
              <div className="flex gap-2 flex-wrap items-center">
                <select
                  ref={copyFromSelectRef}
                  id="copy-from-recipe"
                  className="flex-1 min-w-[180px] px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-sm"
                  value={copyFromMenuItemId}
                  onChange={(e) => setCopyFromMenuItemId(e.target.value)}
                >
                  <option value="">Select a dish to copy from…</option>
                  {otherMenuItemsWithRecipes.map(({ menuItem: m }) => (
                    <option key={m.id} value={String(m.id)}>{m.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCopyFromRecipe();
                  }}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {ingredients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No ingredients added yet.</p>
              <p className="text-sm mt-2">Click &quot;Add Ingredient&quot; to start building the recipe.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ingredients.map((ingredient, index) => (
                <div key={index} className="flex flex-wrap items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  {isCostOnly ? (
                    <>
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product name</label>
                        <input
                          type="text"
                          value={ingredient.name ?? ''}
                          onChange={(e) => handleUpdateIngredient(index, { name: e.target.value })}
                          placeholder="e.g. Chicken breast"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          placeholder="0"
                          value={editingQty[index] !== undefined ? editingQty[index] : (ingredient.quantity === 0 ? '' : String(ingredient.quantity))}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditingQty((prev) => ({ ...prev, [index]: v }));
                            const num = v === '' ? 0 : parseFloat(v);
                            const complete = v === '' || (v !== '.' && !v.endsWith('.') && !Number.isNaN(num));
                            if (complete) handleUpdateIngredient(index, { quantity: v === '' ? 0 : num });
                          }}
                          onBlur={() => {
                            const v = editingQty[index];
                            if (v !== undefined) {
                              const num = v === '' ? 0 : parseFloat(v);
                              handleUpdateIngredient(index, { quantity: Number.isNaN(num) ? 0 : num });
                              setEditingQty((prev) => { const next = { ...prev }; delete next[index]; return next; });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <select
                          value={normalizeUnit(ingredient.unit)}
                          onChange={(e) => handleUpdateIngredient(index, { unit: e.target.value as typeof COST_ONLY_UNITS[number] })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        >
                          {COST_ONLY_UNITS.map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-28">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cost per {normalizeUnit(ingredient.unit)} ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0"
                          value={editingCost[index] !== undefined ? editingCost[index] : (ingredient.costPerUnit === 0 || ingredient.costPerUnit == null ? '' : String(ingredient.costPerUnit))}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditingCost((prev) => ({ ...prev, [index]: v }));
                            const num = v === '' ? 0 : parseFloat(v);
                            const complete = v === '' || (v !== '.' && !v.endsWith('.') && !Number.isNaN(num));
                            if (complete) handleUpdateIngredient(index, { costPerUnit: v === '' ? 0 : num });
                          }}
                          onBlur={() => {
                            const v = editingCost[index];
                            if (v !== undefined) {
                              const num = v === '' ? 0 : parseFloat(v);
                              handleUpdateIngredient(index, { costPerUnit: Number.isNaN(num) ? 0 : num });
                              setEditingCost((prev) => { const next = { ...prev }; delete next[index]; return next; });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        />
                      </div>
                      <span className="text-sm text-gray-500 self-end pb-2">
                        = ${(getIngredientCost(ingredient)).toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient</label>
                        <select
                          value={ingredient.inventoryItemId ?? ''}
                          onChange={(e) => handleUpdateIngredient(index, { inventoryItemId: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        >
                          <option value="">Select inventory item...</option>
                          {inventoryItems.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.name} ({item.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder="0"
                            value={editingQty[index] !== undefined ? editingQty[index] : (ingredient.quantity === 0 ? '' : String(ingredient.quantity))}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditingQty((prev) => ({ ...prev, [index]: v }));
                              const num = v === '' ? 0 : parseFloat(v);
                              const complete = v === '' || (v !== '.' && !v.endsWith('.') && !Number.isNaN(num));
                              if (complete) handleUpdateIngredient(index, { quantity: v === '' ? 0 : num });
                            }}
                            onBlur={() => {
                              const v = editingQty[index];
                              if (v !== undefined) {
                                const num = v === '' ? 0 : parseFloat(v);
                                handleUpdateIngredient(index, { quantity: Number.isNaN(num) ? 0 : num });
                                setEditingQty((prev) => { const next = { ...prev }; delete next[index]; return next; });
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          />
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            {ingredient.inventoryItemId != null ? getInventoryItemUnit(ingredient.inventoryItemId) : ''}
                          </span>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 self-end pb-2">
                        = ${(getIngredientCost(ingredient)).toFixed(2)}
                      </span>
                    </>
                  )}
                  <button
                    onClick={() => handleRemoveIngredient(index)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors mt-6"
                    title="Remove ingredient"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {ingredients.length > 0 && (
            <p className="mt-2 text-sm font-medium text-gray-700">
              Total cost per serve: ${totalCost.toFixed(2)}
            </p>
          )}

          <button
            onClick={handleAddIngredient}
            className="mt-4 w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Ingredient
          </button>
        </div>

        <div className="border-t p-4 flex gap-2">
          <button
            onClick={handleClose}
            className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2 px-4 bg-pink-500 text-white rounded-md font-semibold hover:bg-pink-600 transition-colors"
          >
            Save Recipe
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipeManagementModal;

